/**
 * Managed-vocabulary map for the specialized code-list selectors (SampleStatusSelector, …).
 *
 * A vocabulary is a small, customer-curated code list stored in its own table, whose CODE (not its
 * database id) is what the referencing record persists — `Sample.Status` holds `'PASS'`, not an
 * `IDSampleStatus`. It is read in full and rendered as an option list, so it takes neither the
 * remote-search entity path (see `_SpecializedSelectorMap`) nor a purely static option array.
 *
 * Both stacks read this one definition: the legacy Select-family provider
 * (`PictProvider-InputType-SampleStatusSelector`) and the `Picker` broker's generic option source, so
 * the two render the same list from the same table with the same fallback.
 *
 * Per entry:
 *   Entity    — the vocabulary table (also the meadow scope; the route pluralizes to `<Entity>s`).
 *   ValueField — the column persisted onto the referencing record.
 *   TextField  — the column shown as the label.
 *   SortField  — the column the list is ordered by.
 *   Fallback   — options used when the customer has curated no rows, so the input works uncurated.
 */

/**
 * Fallback sample statuses. Deliberately smaller than the SiteManager set LADOTD runs on (which also
 * carries PREA / TSTR / PASC / FAIC / N/A) — a customer needing those manages them in SampleStatus.
 * @type {Array<{id: string, text: string}>}
 */
const DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS =
[
	{ 'id': 'LOGD', 'text': 'Logged' },
	{ 'id': 'RECV', 'text': 'Received' },
	{ 'id': 'PEND', 'text': 'Pending' },
	{ 'id': 'TEST', 'text': 'In Testing' },
	{ 'id': 'PASS', 'text': 'Passed' },
	{ 'id': 'FAIL', 'text': 'Failed' },
	{ 'id': 'INFO', 'text': 'Info' },
	{ 'id': 'VOID', 'text': 'Voided' },
];

/** @type {Record<string, {Entity: string, ValueField: string, TextField: string, SortField: string, Fallback: Array<{id: string, text: string}>}>} */
const VOCABULARY_SELECTOR_MAP =
{
	SampleStatusSelector:
	{
		Entity: 'SampleStatus',
		ValueField: 'Hash',
		TextField: 'Name',
		SortField: 'Ordinal',
		Fallback: DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS,
	},
};

/**
 * Stamp an input descriptor with its vocabulary's option source, so the shared entity option loader
 * picks it up. Never overwrites a schema-supplied `SelectOptions` / `SelectOptionsEntity`.
 *
 * @param {Object} pInput - The form input descriptor.
 * @param {string} [pInputType] - InputType override; defaults to the descriptor's own.
 * @return {boolean} - True when this input is vocabulary-backed.
 */
function applyVocabularySource(pInput, pInputType)
{
	if (!pInput)
	{
		return false;
	}
	if (!pInput.PictForm)
	{
		pInput.PictForm = {};
	}
	const tmpVocabulary = VOCABULARY_SELECTOR_MAP[pInputType || pInput.PictForm.InputType];
	if (!tmpVocabulary)
	{
		return false;
	}
	if (!Array.isArray(pInput.PictForm.SelectOptions))
	{
		pInput.PictForm.SelectOptions = tmpVocabulary.Fallback;
	}
	if (!pInput.PictForm.SelectOptionsEntity)
	{
		pInput.PictForm.SelectOptionsEntity = tmpVocabulary.Entity;
		pInput.PictForm.SelectOptionsEntityValueField = pInput.PictForm.SelectOptionsEntityValueField || tmpVocabulary.ValueField;
		pInput.PictForm.SelectOptionsEntityTextField = pInput.PictForm.SelectOptionsEntityTextField || tmpVocabulary.TextField;
		pInput.PictForm.SelectOptionsEntitySortField = pInput.PictForm.SelectOptionsEntitySortField || tmpVocabulary.SortField;
	}
	return true;
}

module.exports = { VOCABULARY_SELECTOR_MAP, DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS, applyVocabularySource };
